/**
 * This file contains helpers which can be used to access localStorage,
 * set, get and clear different items. This is done to abstract the access
 * to localStorage so that if we want to change to something else (like Cookies),
 * it is easy.
 * 
 * Every export is prefixed with 'bsh_' (Browser Storage Helper)
 */

import { sfLog } from "./sf_utils";

export const STORAGE_KEY = {
    AUTH_USER: 'authUser',
    AUTH_ADMIN_USER: 'authAdminUser',
    PREV_AUTH_ADMIN_USER: 'prevAuthAdminUser',
    INSURANCE_IO_TOKEN: 'insurance_io_user_token',
    FILTERS: {
        NAME: 'filters',
        TOKEN: 'token',
        CAMPAIGN: 'campaign',
        OVERVIEW: 'overview',
        BILLING: 'billing',
        ANALYTICS: 'analytics',
        CREATIVES: 'creatives',
        AD_GROUPS: 'ad_groups',
        SOURCE_SETTINGS: 'source_settings',
        LEADS: 'leads',
        CALLS: 'calls',
        PROSPECTS: 'prospects'
    }
}

/**
 * Set admin user details
 * @param {object} userInfo Info about admin
 * @param {string} userInfo.token Auth token of admin
 */
export const bsh_setAdminUserDetails = ({ token }) => {
    localStorage.setItem(STORAGE_KEY.AUTH_ADMIN_USER, JSON.stringify({ token }));
}

export const bsh_setInsuranceIoUserDetails = ({ token }) => {
    localStorage.setItem(STORAGE_KEY.INSURANCE_IO_TOKEN, JSON.stringify({ token }));
}

export const bsh_getInsuranceIoUserDetails = () => {
    return JSON.parse(localStorage.getItem(STORAGE_KEY.INSURANCE_IO_TOKEN));
}

/**
 * Clear admin user details
 */
export const bsh_clearAdminUserDetails = () => {
    localStorage.setItem(STORAGE_KEY.AUTH_ADMIN_USER, '{}');
    localStorage.removeItem("adminAccess")
    localStorage.removeItem(STORAGE_KEY.PREV_AUTH_ADMIN_USER);
}

export const bsh_clearInsuranceIoUserDetails = () => {
    localStorage.setItem(STORAGE_KEY.INSURANCE_IO_TOKEN, '{}');
}

/**
 * Get admin user details
 * @returns {object} userInfo Info about admin
 * @returns {object} userInfo.token Token of the admin
 */
export const bsh_getAdminUserDetails = () => {
    let userInfo = null;
    try {
        userInfo = JSON.parse(localStorage.getItem(STORAGE_KEY.AUTH_ADMIN_USER));
    } catch (e) {
        sfLog('[WARN] Failed to parse admin token');
    }

    return {
        token: userInfo?.token
    }
}

export const bsh_isAdminUserDetailsPresent = () => {
    return !!bsh_getAdminUserDetails().token;
}

/**
 * Set current and prev admin user details
 * @param {string} prev_token Prev Auth token of admin
 * @param {string} current_token Auth token of admin
 */
export const bsh_setCurrentToPrevAdminUserDetails = (prev_token, current_token) => {
    localStorage.setItem(STORAGE_KEY.PREV_AUTH_ADMIN_USER, JSON.stringify({ token: prev_token }));
    localStorage.setItem(STORAGE_KEY.AUTH_ADMIN_USER, JSON.stringify({ token: current_token }));
}

/**
 * Set prev to current admin user details
 * @param {{prev_token: string, current_token: string}} tokens Prev and Current Auth token of admin
 */
export const bsh_setPrevToCurrentAdminUserDetails = (tokens) => {
    localStorage.setItem(STORAGE_KEY.AUTH_ADMIN_USER, JSON.stringify({ token: tokens.prev_token }));
    localStorage.removeItem(STORAGE_KEY.PREV_AUTH_ADMIN_USER);
}

/**
 * Get prev to current admin user details
 * @returns {{prev_token: string, current_token: string}} tokens Prev and Current Auth token of admin 
 */
export const bsh_getBothPrevAndCurrentAdminUserDetails = () => {
    let prev_token = null;
    let current_token = null;
    try {
        current_token = JSON.parse(localStorage.getItem(STORAGE_KEY.AUTH_ADMIN_USER));
        prev_token = JSON.parse(localStorage.getItem(STORAGE_KEY.PREV_AUTH_ADMIN_USER));
    } catch (e) {
        sfLog('[WARN] Failed to parse admin token');
    }

    return {
        prev_token: prev_token?.token,
        current_token: current_token?.token
    }
}

export const bsh_getClientUserDetails = () => {
    let userInfo = null;
    try {
        userInfo = JSON.parse(localStorage.getItem(STORAGE_KEY.AUTH_USER)).user
    } catch (e) {
        sfLog('[WARN] Failed to parse admin token');
    }

    return userInfo;
}

/**
 * @brief Set the campaign filters in local storage
 * 
 * @param {string} filters component name of the filters
 * @param {string} field field of the filter
 * @param {any} value value of the filter
 */
export const bsh_setFilters = (filters, field, value) => {
    let filtersObj = JSON.parse(localStorage.getItem(STORAGE_KEY.FILTERS.NAME));
    if (!filtersObj) {
        let data = {
            [filters]: {
                [field]: value
            }
        }
        localStorage.setItem(STORAGE_KEY.FILTERS.NAME, JSON.stringify(data));
        return;
    } else {
        if (filtersObj?.[filters]) {
            filtersObj[filters][field] = value;
            localStorage.setItem(STORAGE_KEY.FILTERS.NAME, JSON.stringify(filtersObj));
        } else {
            filtersObj[filters] = {
                [field]: value
            }
            localStorage.setItem(STORAGE_KEY.FILTERS.NAME, JSON.stringify(filtersObj));
        }
    }
}

/**
 * @brief Get the campaign filters from local storage
 * 
 * @param {string} filters component name of the filters
 * @param {string} field field of the filter
 * @returns value of the filter
 */
export const bsh_getFilters = (filters, field) => {
    return JSON.parse(localStorage.getItem(STORAGE_KEY.FILTERS.NAME))?.[filters]?.[field];
}

/**
 * @brief get the token of the user who last updated the browser storage filters
 * 
 * @returns token of the user
 */
export const bsh_getTokenForFilters = () => {
    return JSON.parse(localStorage.getItem(STORAGE_KEY.FILTERS.NAME))?.[STORAGE_KEY.FILTERS.TOKEN];
}

/** 
 * @brief set the user token in filters browser storage
 * 
 * @param {object} token token of the user
 */
export const bsh_setTokenForFilters = (token) => {
    localStorage.setItem(STORAGE_KEY.FILTERS.NAME, JSON.stringify({ [STORAGE_KEY.FILTERS.TOKEN]: token }));
}